import { useMemo, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useAddAdditionalContact,
  useCompanyContacts,
  useContactLookup,
  useRemoveAdditionalContact,
  useTicketAdditionalContacts,
  type Contact,
} from '@/api/hooks';

interface Props {
  ticketId: number;
  companyId: number | null;
  /** The ticket's primary contact — already on the ticket, so not offered as a CC. */
  primaryContactId: number | null;
  className?: string;
}

function contactName(c: Pick<Contact, 'firstName' | 'lastName'>): string {
  return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
}

/**
 * The ticket's CC'd contacts (`TicketAdditionalContacts`), with add/remove. These are the
 * people Autotask notifies alongside the primary contact, which is why the Log Time modal
 * lists them as recipients.
 */
export function AdditionalContactsPicker({ ticketId, companyId, primaryContactId, className }: Props) {
  const links = useTicketAdditionalContacts(ticketId);
  const companyContacts = useCompanyContacts(companyId);
  const add = useAddAdditionalContact();
  const remove = useRemoveAdditionalContact();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const linked = links.data?.items ?? [];
  // The company list covers most cases, but a CC'd contact from elsewhere still needs a name.
  const lookup = useContactLookup(linked.map((l) => l.contactID));

  const nameFor = (contactId: number): { name: string; email: string | null } => {
    const c =
      companyContacts.data?.items.find((x) => x.id === contactId) ??
      lookup.data?.items.find((x) => x.id === contactId);
    return c
      ? { name: contactName(c) || `#${contactId}`, email: c.emailAddress ?? null }
      : { name: `#${contactId}`, email: null };
  };

  const alreadyOn = new Set([
    ...linked.map((l) => l.contactID),
    ...(primaryContactId != null ? [primaryContactId] : []),
  ]);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (companyContacts.data?.items ?? [])
      .filter((c) => !alreadyOn.has(c.id))
      .filter((c) => !q || contactName(c).toLowerCase().includes(q) || (c.emailAddress ?? '').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyContacts.data, query, linked, primaryContactId]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const addContact = (c: Contact) => {
    add.mutate(
      { ticketId, contactID: c.id },
      {
        onSuccess: () => { toast.success(`CC'd ${contactName(c) || `#${c.id}`}`); setOpen(false); },
        onError: (err) => toast.error('Failed to add contact', {
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
      },
    );
  };

  const removeContact = (linkId: number, label: string) => {
    remove.mutate(
      { ticketId, linkId },
      {
        onSuccess: () => toast.success(`Removed ${label}`),
        onError: (err) => toast.error('Failed to remove contact', {
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
      },
    );
  };

  return (
    <div className={cn('flex flex-col items-end gap-1', className)}>
      {linked.map((link) => {
        const { name, email } = nameFor(link.contactID);
        return (
          <div key={link.id} className="flex max-w-full items-start gap-1">
            <span className="flex min-w-0 flex-col items-end">
              <span className="truncate text-sm">{name}</span>
              {email && <span className="truncate text-[11px] text-muted-foreground">{email}</span>}
            </span>
            <button
              type="button"
              onClick={() => removeContact(link.id, name)}
              disabled={remove.isPending}
              className="mt-[3px] shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
              aria-label={`Remove ${name}`}
              title="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}

      {linked.length === 0 && !links.isLoading && (
        <span className="text-sm text-muted-foreground">None</span>
      )}
      {links.isLoading && <span className="text-sm text-muted-foreground">Loading…</span>}

      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger
          disabled={companyId == null || add.isPending}
          className={cn(
            'flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-muted-foreground',
            'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:opacity-40',
          )}
        >
          {add.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Add
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72 overflow-hidden p-0">
          <div className="p-2">
            <Input
              autoFocus
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <DropdownMenuSeparator className="my-0" />
          <div className="max-h-72 overflow-y-auto overflow-x-hidden py-1">
            {companyContacts.isLoading && <DropdownMenuItem disabled>Loading contacts…</DropdownMenuItem>}
            {!companyContacts.isLoading && options.length === 0 && (
              <DropdownMenuItem disabled>
                {companyId == null ? 'No company on ticket' : 'No contacts left to add'}
              </DropdownMenuItem>
            )}
            {options.map((c) => (
              <DropdownMenuItem
                key={c.id}
                onSelect={(e) => { e.preventDefault(); addContact(c); }}
                className="flex flex-col items-start gap-0.5"
              >
                <span className="truncate">{contactName(c) || `#${c.id}`}</span>
                {c.emailAddress && (
                  <span className="truncate text-[11px] text-muted-foreground">{c.emailAddress}</span>
                )}
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
